import { z } from 'zod';
import { MoneyLevelSchema } from './schemas';

/**
 * Отчёты и настройка списков (раздел 6.18, АН-05, АН-07, АН-09, АН-10).
 * Источник данных — описанный набор полей раздела портала; вид (колонки, отбор, группировка, сортировка, итоги)
 * один и тот же для страницы-списка, для конструктора и для выгрузки. Поля с суммами у источника помечены
 * уровнем денег: без него поля в ответе нет и использовать его в отборе, сортировке, группировке и итогах нельзя.
 */

export const REPORT_LIMITS = {
  /** Строк в выгрузке в Excel и PDF */
  exportRows: 50_000,
  /** Строк источника, которые читает один запрос */
  sourceRows: 20_000,
  pageSize: 200,
  /** Строк в одной группе в ответе */
  groupRows: 200,
} as const;

export const FIELD_TYPES = {
  text: 'Текст',
  number: 'Число',
  date: 'Дата',
  enum: 'Список',
  bool: 'Да / нет',
} as const;
export type FieldType = keyof typeof FIELD_TYPES;
export const FieldTypeSchema = z.enum(['text', 'number', 'date', 'enum', 'bool']);

/** Единица числа: для подписи и формата в Excel */
export const FieldUnitSchema = z.enum(['money', 'pct', 'days', 'qty']);

export const SourceField = z.object({
  key: z.string(),
  label: z.string(),
  type: FieldTypeSchema,
  /** Минимальный уровень денег, с которого поле видно; null — не деньги */
  money: MoneyLevelSchema.nullable(),
  unit: FieldUnitSchema.nullable(),
  /** Можно группировать (список, текст, дата) */
  groupable: z.boolean(),
  /** Можно считать итоги (число) */
  aggregatable: z.boolean(),
  /** Значения списка: значение → подпись */
  options: z.array(z.object({ value: z.string(), label: z.string() })).nullable(),
  /** Поле-ссылка: в строке есть `<key>__href` с адресом записи портала */
  linked: z.boolean(),
});
export type SourceField = z.infer<typeof SourceField>;

export const SourceInfo = z.object({
  key: z.string(),
  title: z.string(),
  group: z.string(),
  description: z.string(),
  fields: z.array(SourceField),
  defaultColumns: z.array(z.string()),
  /** Поле даты для периода, календаря и среза; null — у источника дат нет */
  dateField: z.string().nullable(),
  /** Есть ли у записей координаты для карты (пока нет ни у одного источника) */
  hasMap: z.boolean(),
  /** Часть полей скрыта от этого человека уровнем денег */
  moneyHidden: z.boolean(),
});
export type SourceInfo = z.infer<typeof SourceInfo>;

export const FILTER_OPS = {
  eq: 'равно',
  ne: 'не равно',
  contains: 'содержит',
  gt: 'больше',
  gte: 'не меньше',
  lt: 'меньше',
  lte: 'не больше',
  between: 'между',
  in: 'одно из',
  empty: 'пусто',
  notempty: 'не пусто',
} as const;
export type FilterOp = keyof typeof FILTER_OPS;
export const FilterOpSchema = z.enum([
  'eq',
  'ne',
  'contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
  'empty',
  'notempty',
]);

const Scalar = z.union([z.string().max(500), z.number().finite(), z.boolean()]);

export const ViewFilter = z.object({
  field: z.string().max(80),
  op: FilterOpSchema,
  value: Scalar.nullish(),
  /** Второе значение для «между» */
  value2: Scalar.nullish(),
  /** Список значений для «одно из» */
  values: z.array(Scalar).max(100).nullish(),
});
export type ViewFilter = z.infer<typeof ViewFilter>;

export const AGG_FNS = {
  sum: 'Сумма',
  count: 'Количество',
  avg: 'Среднее',
  min: 'Минимум',
  max: 'Максимум',
} as const;
export type AggFn = keyof typeof AGG_FNS;
export const AggFnSchema = z.enum(['sum', 'count', 'avg', 'min', 'max']);

export const VIEW_KINDS = {
  table: 'Таблица',
  board: 'Доска',
  calendar: 'Календарь',
  chart: 'График',
  map: 'Карта',
} as const;
export type ViewKind = keyof typeof VIEW_KINDS;
export const ViewKindSchema = z.enum(['table', 'board', 'calendar', 'chart', 'map']);

export const ViewColumn = z.object({
  key: z.string().max(80),
  visible: z.boolean().default(true),
  width: z.number().int().min(40).max(800).nullish(),
});
export type ViewColumn = z.infer<typeof ViewColumn>;

export const ViewConfig = z.object({
  /** Колонки в порядке показа; пусто — колонки источника по умолчанию */
  columns: z.array(ViewColumn).max(60).default([]),
  filters: z.array(ViewFilter).max(20).default([]),
  groupBy: z.array(z.string().max(80)).max(2).default([]),
  sort: z
    .array(z.object({ field: z.string().max(80), dir: z.enum(['asc', 'desc']).default('asc') }))
    .max(3)
    .default([]),
  /** Итоги: по всей выборке и по каждой группе */
  totals: z
    .array(z.object({ field: z.string().max(80), fn: AggFnSchema }))
    .max(10)
    .default([]),
  viewKind: ViewKindSchema.default('table'),
  /** Доска: по какому полю-списку делить на колонки */
  boardField: z.string().max(80).nullish(),
  /** График: ось X (группа), значение Y и функция; тип рисунка выбирает страница */
  chart: z
    .object({
      x: z.string().max(80),
      y: z.string().max(80).nullish(),
      fn: AggFnSchema.default('count'),
      kind: z.enum(['bar', 'line', 'pie']).default('bar'),
    })
    .nullish(),
  /** Период по полю даты источника */
  period: z
    .object({
      field: z.string().max(80),
      from: z.string().date().nullish(),
      to: z.string().date().nullish(),
    })
    .nullish(),
});
export type ViewConfig = z.infer<typeof ViewConfig>;

export const ViewRow = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
export type ViewRow = z.infer<typeof ViewRow>;

export const GroupResult = z.object({
  /** Значения группировки (подписи) по порядку полей groupBy */
  keys: z.array(z.string()),
  count: z.number().int(),
  totals: z.record(z.string(), z.number().nullable()),
  rows: z.array(ViewRow),
  /** Строки группы показаны не все */
  truncated: z.boolean(),
});
export type GroupResult = z.infer<typeof GroupResult>;

export const ListResponse = z.object({
  source: z.string(),
  title: z.string(),
  /** Видимые колонки в порядке показа */
  columns: z.array(SourceField),
  rows: z.array(ViewRow),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** Итоги по всей выборке: ключ `<fn>:<field>` */
  totals: z.record(z.string(), z.number().nullable()),
  groups: z.array(GroupResult).nullable(),
  chart: z
    .object({
      kind: z.enum(['bar', 'line', 'pie']),
      xLabel: z.string(),
      yLabel: z.string(),
      points: z.array(z.object({ label: z.string(), value: z.number().nullable() })),
    })
    .nullable(),
  calendar: z
    .array(
      z.object({
        date: z.string(),
        items: z.array(z.object({ id: z.string(), title: z.string(), href: z.string().nullable() })),
      }),
    )
    .nullable(),
  /** Дата среза и честная оговорка, что именно на неё пересчитано */
  asOf: z.string(),
  asOfNote: z.string().nullable(),
  /** Строк в источнике больше, чем прочитано (лимит) */
  truncated: z.boolean(),
  moneyHidden: z.boolean(),
});
export type ListResponse = z.infer<typeof ListResponse>;

export const RowsQuery = z.object({
  view: z.string().max(20_000).optional(),
  viewId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(REPORT_LIMITS.pageSize).default(50),
  asOf: z.string().date().optional(),
});
export type RowsQuery = z.infer<typeof RowsQuery>;

/* ─────────── Сохранённые виды списков (АН-09) ─────────── */

export const ListView = z.object({
  id: z.uuid(),
  page: z.string(),
  name: z.string(),
  shared: z.boolean(),
  mine: z.boolean(),
  ownerName: z.string().nullable(),
  config: ViewConfig,
  updatedAt: z.string(),
});
export type ListView = z.infer<typeof ListView>;

export const ListViewInput = z.object({
  page: z.string().min(1).max(60),
  name: z.string().trim().min(1).max(80),
  shared: z.boolean().default(false),
  config: ViewConfig,
});
export type ListViewInput = z.infer<typeof ListViewInput>;

export const ListViewPatch = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  shared: z.boolean().optional(),
  config: ViewConfig.optional(),
});
export type ListViewPatch = z.infer<typeof ListViewPatch>;

export const ViewPref = z.object({
  page: z.string(),
  viewId: z.uuid().nullable(),
  config: ViewConfig.nullable(),
});
export type ViewPref = z.infer<typeof ViewPref>;

export const ViewPrefInput = z.object({
  viewId: z.uuid().nullish(),
  config: ViewConfig.nullish(),
});
export type ViewPrefInput = z.infer<typeof ViewPrefInput>;

/* ─────────── Конструктор отчётов (АН-05) ─────────── */

export const CustomReport = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  source: z.string(),
  shared: z.boolean(),
  mine: z.boolean(),
  ownerName: z.string().nullable(),
  config: ViewConfig,
  /** Ссылка внутри портала */
  path: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomReport = z.infer<typeof CustomReport>;

export const CustomReportInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).nullish(),
  source: z.string().min(1).max(60),
  shared: z.boolean().default(false),
  config: ViewConfig,
});
export type CustomReportInput = z.infer<typeof CustomReportInput>;

export const CustomReportPatch = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(2000).nullish(),
  source: z.string().min(1).max(60).optional(),
  shared: z.boolean().optional(),
  config: ViewConfig.optional(),
});
export type CustomReportPatch = z.infer<typeof CustomReportPatch>;

export const PreviewInput = z.object({
  source: z.string().min(1).max(60),
  config: ViewConfig,
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(REPORT_LIMITS.pageSize).default(50),
  asOf: z.string().date().nullish(),
});
export type PreviewInput = z.infer<typeof PreviewInput>;

/* ─────────── Выгрузка (АН-10) ─────────── */

export const ExportInput = z.object({
  source: z.string().min(1).max(60),
  config: ViewConfig,
  format: z.enum(['xlsx', 'pdf']),
  asOf: z.string().date().nullish(),
  title: z.string().trim().max(120).nullish(),
});
export type ExportInput = z.infer<typeof ExportInput>;

/* ─────────── Готовые отчёты (АН-07) ─────────── */

export const READY_REPORTS = {
  weekly_customer: 'Еженедельный отчёт заказчику',
  manager_digest: 'Сводка руководителю',
  plan_fact: 'План-факт к совещанию',
  contractor: 'Отчёт по подрядчику',
} as const;
export type ReadyReportKind = keyof typeof READY_REPORTS;
export const ReadyReportKindSchema = z.enum(['weekly_customer', 'manager_digest', 'plan_fact', 'contractor']);

export const DocColumn = z.object({
  key: z.string(),
  label: z.string(),
  /** Как показывать значение: деньги, процент, число, дата, текст */
  format: z.enum(['text', 'money', 'pct', 'number', 'date']).default('text'),
  align: z.enum(['left', 'right']).default('left'),
});
export type DocColumn = z.infer<typeof DocColumn>;

export const DocCell = z.union([z.string(), z.number(), z.null()]);

/** Единый вид документа: из него собираются Word, PDF и страница портала */
export const DocSection = z.object({
  heading: z.string(),
  paragraphs: z.array(z.string()).default([]),
  kpis: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
  table: z
    .object({
      columns: z.array(DocColumn),
      rows: z.array(z.array(DocCell)),
      /** Ссылки на записи портала по строкам (параллельно rows), если есть */
      links: z.array(z.string().nullable()).default([]),
    })
    .nullable()
    .default(null),
});
export type DocSection = z.infer<typeof DocSection>;

export const ReportDoc = z.object({
  title: z.string(),
  subtitle: z.string().nullable(),
  sections: z.array(DocSection),
  /** Оговорки: что не вошло и почему (например, суммы скрыты уровнем денег) */
  notes: z.array(z.string()),
});
export type ReportDoc = z.infer<typeof ReportDoc>;

export const ReportRun = z.object({
  id: z.uuid(),
  kind: ReadyReportKindSchema,
  kindTitle: z.string(),
  number: z.string(),
  projectId: z.uuid().nullable(),
  projectCode: z.string().nullable(),
  title: z.string(),
  periodFrom: z.string().nullable(),
  periodTo: z.string().nullable(),
  asOf: z.string(),
  origin: z.enum(['manual', 'schedule']),
  moneyShown: z.boolean(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
});
export type ReportRun = z.infer<typeof ReportRun>;

export const ReportRunFull = ReportRun.extend({ doc: ReportDoc });
export type ReportRunFull = z.infer<typeof ReportRunFull>;

export const ReadyReportInput = z.object({
  kind: ReadyReportKindSchema,
  /** Проект — для еженедельного отчёта заказчику и план-факта */
  projectId: z.uuid().nullish(),
  /** Подрядчик (контрагент) — для отчёта по подрядчику */
  counterpartyId: z.uuid().nullish(),
  /** Неделя: любая дата внутри недели; по умолчанию текущая */
  weekOf: z.string().date().nullish(),
  /** Дата среза; по умолчанию сегодня */
  asOf: z.string().date().nullish(),
});
export type ReadyReportInput = z.infer<typeof ReadyReportInput>;

export const RunsQuery = z.object({
  kind: ReadyReportKindSchema.optional(),
  projectId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type RunsQuery = z.infer<typeof RunsQuery>;

export const DigestPref = z.object({
  enabled: z.boolean(),
  hour: z.number().int().min(0).max(23),
  lastRunOn: z.string().nullable(),
});
export type DigestPref = z.infer<typeof DigestPref>;

export const DigestPrefInput = z.object({
  enabled: z.boolean(),
  hour: z.number().int().min(0).max(23).default(8),
});
export type DigestPrefInput = z.infer<typeof DigestPrefInput>;

/* ─────────── Pilot (АН-06) ─────────── */

export const PilotReportInput = z.object({ question: z.string().trim().min(3).max(1000) });
export type PilotReportInput = z.infer<typeof PilotReportInput>;
